import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Fade,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

const drawerWidth = 240;

const formatDate = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const toFormatYYYYMMDD = (ddmmyyyy) => {
  if (!ddmmyyyy) return "";
  const [dd, mm, yyyy] = ddmmyyyy.split("-");
  return `${yyyy}-${mm}-${dd}`;
};

function AttendanceForm() {
  const [selectedTeacher, setSelectedTeacher] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));
  const [selectedStream, setSelectedStream] = useState("0");
  const [studentList, setStudentList] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [streams, setStreams] = useState([]);
  const [presentAll, setPresentAll] = useState(false);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  // Select open states for lazy loading
  const [teacherSelectOpen, setTeacherSelectOpen] = useState(false);
  const [classSelectOpen, setClassSelectOpen] = useState(false);
  const [sectionSelectOpen, setSectionSelectOpen] = useState(false);
  const [streamSelectOpen, setStreamSelectOpen] = useState(false);

  const token = sessionStorage.getItem("token");

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const showSnackbar = (message, severity = "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const fetchTeachers = async () => {
    try {
      const response = await fetch(
        "https://wsms.aidigitalsec.com/api/Attendance/GetTeacherList",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        setTeachers(data);
      } else if (data && Array.isArray(data.teachers) && data.teachers.length > 0) {
        setTeachers(data.teachers);
      } else {
        setTeachers([]);
      }
    } catch (error) {
      console.error("Error fetching teachers:", error);
      setTeachers([]);
      showSnackbar("Failed to load teachers.", "error");
    }
  };

  const fetchClasses = async (teacherId) => {
    try {
      const response = await fetch(
        "https://wsms.aidigitalsec.com/api/Teacher/GetClass",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ teacherId }),
        }
      );
      const data = await response.json();
      if (data.data === null && data.msg === "Record Not Found") {
        setClasses([]);
        return;
      }
      if (Array.isArray(data) && data.length > 0) {
        setClasses(data);
      } else {
        setClasses([]);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
      setClasses([]);
      showSnackbar("Failed to load classes.", "error");
    }
  };

  const fetchSections = async (teacherId, classId) => {
    try {
      const response = await fetch(
        "https://wsms.aidigitalsec.com/api/Teacher/ddlSection_clsId",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ teacherId, classId }),
        }
      );
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        setSections(data);
      } else {
        setSections([]);
      }
    } catch (error) {
      console.error("Error fetching sections:", error);
      setSections([]);
      showSnackbar("Failed to load sections.", "error");
    }
  };

  const fetchStreams = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/Stream/GetAllStreams`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ streamID: 0 }),
      });
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        setStreams(data);
      } else if (data && Array.isArray(data.streams) && data.streams.length > 0) {
        setStreams(data.streams);
      } else {
        setStreams([]);
      }
    } catch (error) {
      console.error("Error fetching streams:", error);
      setStreams([]);
      showSnackbar("Failed to load streams.", "error");
    }
  };

  const fetchStudents = async () => {
    if (!selectedClass || !selectedSection) {
      setStudentList([]);
      setFilteredStudents([]);
      return;
    }

    setStudentsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const payload = {
        stream: selectedStream || "0",
        classId: selectedClass,
        sessionId: 0,
      };
      const response = await fetch(`${apiUrl}/Attendance/GetStudentAdmissionDetails`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to fetch students");

      const data = await response.json();
      if (!data || data.length === 0 || data.msg === "Record Not Found") {
        setStudentList([]);
        setFilteredStudents([]);
        return;
      }

      const initialized = data.map((s) => ({ ...s, attendance: "Present" }));
      setStudentList(initialized);
    } catch (error) {
      console.error(error);
      setStudentList([]);
      setFilteredStudents([]);
      showSnackbar("Failed to load students.", "error");
    } finally {
      setStudentsLoading(false);
    }
  };

  useEffect(() => {
    fetchStreams();
    fetchTeachers();
  }, []);

  useEffect(() => {
    if (selectedTeacher) fetchClasses(selectedTeacher);
    else {
      setClasses([]);
      setSelectedClass("");
    }
  }, [selectedTeacher]);

  useEffect(() => {
    if (selectedTeacher && selectedClass) fetchSections(selectedTeacher, selectedClass);
    else {
      setSections([]);
      setSelectedSection("");
    }
  }, [selectedClass, selectedTeacher]);

  useEffect(() => {
    if (selectedClass && selectedSection) fetchStudents();
  }, [selectedClass, selectedSection, selectedStream]);

  useEffect(() => {
    if (selectedStream === "0") {
      setFilteredStudents(studentList);
    } else {
      setFilteredStudents(studentList.filter((s) => s.stream === selectedStream));
    }
  }, [studentList, selectedStream]);

  const handleDateChange = (e) => {
    let dateValue = e.target.value;
    if (!dateValue) {
      setSelectedDate(formatDate(new Date()));
      return;
    }
    const selected = new Date(dateValue + "T12:00:00Z");
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    if (selected > today) {
      dateValue = today.toISOString().split("T")[0];
    }
    const [year, month, day] = dateValue.split("-");
    setSelectedDate(`${day}-${month}-${year}`);
  };

  const handleAttendanceChange = (id, status) => {
    setStudentList((prev) =>
      prev.map((student) =>
        student.studentId === id ? { ...student, attendance: status } : student
      )
    );
  };

  const handlePresentAllChange = (e) => {
    const checked = e.target.checked;
    setPresentAll(checked);
    setStudentList((prev) =>
      prev.map((student) => ({
        ...student,
        attendance: checked ? "Present" : "",
      }))
    );
  };

  const handleSubmitAttendance = async () => {
    const attendanceData = filteredStudents.map((student) => ({
      studentId: student.studentId,
      isAbsent: student.attendance === "Absent",
      isPresent: student.attendance === "Present",
      isWeekOff: student.attendance === "Week Off",
    }));

    const formattedDate = selectedDate.split("-").reverse().join("/");

    const payload = {
      classId: selectedClass,
      sectionId: selectedSection,
      attendenceDate: formattedDate,
      attendence: attendanceData,
    };

    try {
      const response = await fetch(
        "https://wsms.aidigitalsec.com/api/Teacher/SaveAttendance",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error("Failed to save");

      showSnackbar("Attendance marked successfully!", "success");

      setSelectedTeacher("");
      setSelectedClass("");
      setSelectedSection("");
      setSelectedStream("0");
      setSelectedDate(formatDate(new Date()));
      setStudentList([]);
      setFilteredStudents([]);
      setPresentAll(false);
    } catch (error) {
      console.error(error);
      showSnackbar("Failed to save attendance.", "error");
    }
  };

  const handleCancel = () => {
    window.location.reload();
  };

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        width: { md: `calc(100% - ${drawerWidth}px)` },
        ml: { md: `${drawerWidth}px` },
        mt: { xs: 7, sm: 8 },
        p: { xs: 2, md: 4 },
        bgcolor: "linear-gradient(135deg, #f5f5f5, #ffffff)",
        minHeight: "100vh",
        borderRadius: { xs: 0, md: 2 },
      }}
    >
      <Fade in timeout={500}>
        <Stack spacing={4} sx={{ maxWidth: { xs: "100%", lg: 1400 }, mx: "auto" }}>
          {/* Main Card Wrapper - Wider */}
          <Card
            sx={{
              borderRadius: 4,
              boxShadow: "0 10px 28px rgba(0,0,0,0.08)",
              bgcolor: "linear-gradient(180deg, #f8fafc, #ffffff)",
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              {/* Title - Darker text for better readability */}
           <Box
  sx={{
    bgcolor: "linear-gradient(145deg, #f59e0b 0%, #d97706 100%)",
    borderRadius: 2,
    py: 2.5,
    px: 4,
    textAlign: "center",
    mb: 4,
  }}
>

                <Typography
                  variant="h4"
                  sx={{
                    color: "#000000", // Pure white for strong contrast
                    fontWeight: 700,
                    fontSize: { xs: "1.75rem", md: "2.2rem" },
                    textShadow: "1px 1px 3px rgba(0,0,0,0.3)", // Added subtle shadow for extra readability
                  }}
                >
                  Attendance Form
                </Typography>
              </Box>

              {/* Filters - Wider grid */}
              <Grid container spacing={4} sx={{ mb: 4 }}>
                {/* Teacher */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel
  sx={{
    fontWeight: 600,
    color: "#d97706",
    textTransform: "uppercase",
    fontSize: "0.85rem",
    letterSpacing: "0.5px",
  }}
>

                      Teacher
                    </InputLabel>
                    <Select
                      value={selectedTeacher}
                      label="Teacher"
                      onChange={(e) => setSelectedTeacher(e.target.value)}
                      open={teacherSelectOpen}
                      onOpen={async () => {
                        setTeacherSelectOpen(true);
                        if (teachers.length === 0) await fetchTeachers();
                      }}
                      onClose={() => setTeacherSelectOpen(false)}
                      sx={{
                        "& .MuiOutlinedInput-input": { py: 1.5 },
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#ddd" },
                       "&:hover .MuiOutlinedInput-notchedOutline": {
  borderColor: "#d97706",
},
"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
  borderWidth: 2,
  borderColor: "#d97706",
  boxShadow: "0 0 0 2px rgba(217,119,6,0.25)",
},

                      }}
                    >
                      <MenuItem value="" disabled>
                        Select Teacher
                      </MenuItem>
                      {teachers.map((t) => (
                        <MenuItem key={t.employeeId} value={t.employeeId}>
                          {t.employeeName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Class */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth disabled={!selectedTeacher}>
                    <InputLabel
  sx={{
    fontWeight: 600,
    color: "#d97706",
    textTransform: "uppercase",
    fontSize: "0.85rem",
    letterSpacing: "0.5px",
  }}
>

                      Class
                    </InputLabel>
                    <Select
                      value={selectedClass}
                      label="Class"
                      onChange={(e) => setSelectedClass(e.target.value)}
                      open={classSelectOpen}
                      onOpen={async () => {
                        if (!selectedTeacher) {
                          showSnackbar("Select a teacher first.", "warning");
                          return;
                        }
                        setClassSelectOpen(true);
                        if (classes.length === 0) await fetchClasses(selectedTeacher);
                      }}
                      onClose={() => setClassSelectOpen(false)}
                      sx={{
                        "& .MuiOutlinedInput-input": { py: 1.5 },
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#ddd" },
                       "&:hover .MuiOutlinedInput-notchedOutline": {
  borderColor: "#d97706",
},
"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
  borderWidth: 2,
  borderColor: "#d97706",
  boxShadow: "0 0 0 2px rgba(217,119,6,0.25)",
},

                      }}
                    >
                      <MenuItem value="" disabled>
                        Select Class
                      </MenuItem>
                      {classes.map((c) => (
                        <MenuItem key={c.classId} value={c.classId}>
                          {c.className}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Section */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth disabled={!selectedClass}>
                    <InputLabel
  sx={{
    fontWeight: 600,
    color: "#d97706",
    textTransform: "uppercase",
    fontSize: "0.85rem",
    letterSpacing: "0.5px",
  }}
>

                      Section
                    </InputLabel>
                    <Select
                      value={selectedSection}
                      label="Section"
                      onChange={(e) => setSelectedSection(e.target.value)}
                      open={sectionSelectOpen}
                      onOpen={async () => {
                        if (!selectedClass) {
                          showSnackbar("Select a class first.", "warning");
                          return;
                        }
                        setSectionSelectOpen(true);
                        if (sections.length === 0) await fetchSections(selectedTeacher, selectedClass);
                      }}
                      onClose={() => setSectionSelectOpen(false)}
                      sx={{
                        "& .MuiOutlinedInput-input": { py: 1.5 },
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#ddd" },
                       "&:hover .MuiOutlinedInput-notchedOutline": {
  borderColor: "#d97706",
},
"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
  borderWidth: 2,
  borderColor: "#d97706",
  boxShadow: "0 0 0 2px rgba(217,119,6,0.25)",
},

                      }}
                    >
                      <MenuItem value="" disabled>
                        Select Section
                      </MenuItem>
                      {sections.map((s) => (
                        <MenuItem key={s.sectionId} value={s.sectionId}>
                          {s.sectionName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Date */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date"
                    value={selectedDate ? toFormatYYYYMMDD(selectedDate) : ""}
                    onChange={handleDateChange}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: new Date().toISOString().split("T")[0] }}
                    sx={{
                      "& .MuiOutlinedInput-input": { py: 1.5 },
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#ddd" },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
  borderColor: "#d97706",
},
"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
  borderWidth: 2,
  borderColor: "#d97706",
  boxShadow: "0 0 0 2px rgba(217,119,6,0.25)",
},
"& .MuiInputLabel-root": {
  fontWeight: 600,
  color: "#d97706",
  fontSize: "0.85rem",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
},

                    }}
                  />
                </Grid>

                {/* Stream - Full row on small screens, half on large */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth disabled={!selectedSection}>
                    <InputLabel
  sx={{
    fontWeight: 600,
    color: "#d97706",
    textTransform: "uppercase",
    fontSize: "0.85rem",
    letterSpacing: "0.5px",
  }}
>

                      Stream
                    </InputLabel>
                    <Select
                      value={selectedStream}
                      label="Stream"
                      onChange={(e) => setSelectedStream(e.target.value)}
                      sx={{
                        "& .MuiOutlinedInput-input": { py: 1.5 },
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#ddd" },
                     "&:hover .MuiOutlinedInput-notchedOutline": {
  borderColor: "#d97706",
},
"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
  borderWidth: 2,
  borderColor: "#d97706",
  boxShadow: "0 0 0 2px rgba(217,119,6,0.25)",
},

                      }}
                    >
                      <MenuItem value="0">All Streams</MenuItem>
                      {streams.map((s) => (
                        <MenuItem key={s.streamID || s.streamId} value={s.streamName}>
                          {s.streamName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Student List - Same fixes for heading */}
              {studentsLoading ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8 }}>
                  <CircularProgress sx={{ color: "#00796b" }} size={60} />
                  <Typography sx={{ mt: 3 }}>Loading students...</Typography>
                </Box>
              ) : filteredStudents.length > 0 ? (
                <>
                 <Box
  sx={{
    bgcolor: "linear-gradient(145deg, #f59e0b 0%, #d97706 100%)",
    borderRadius: 2,
    py: 1.5,
    px: 3,
    textAlign: "center",
    mb: 3,
  }}
>

                    <Typography variant="h5" sx={{ 
                      color: "#000000",
                      fontWeight: 600,
                      textShadow: "1px 1px 3px rgba(0,0,0,0.3)",
                    }}>
                      Student List ({filteredStudents.length})
                    </Typography>
                  </Box>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={presentAll}
                        onChange={handlePresentAllChange}
                        sx={{
  color: "#d97706",
  "&.Mui-checked": { color: "#d97706" },
}}
                      />
                    }
                    label={<Typography sx={{ fontWeight: "bold", color: "#333" }}>Mark All as Present</Typography>}
                    sx={{
                      bgcolor: "#f1f5f9",
                      px: 3,
                      py: 1.5,
                      borderRadius: 2,
                      mb: 3,
                      alignSelf: "flex-start",
                    }}
                  />

                <TableContainer
  sx={{
    maxHeight: 520,
    overflowX: "hidden",
    "&::-webkit-scrollbar": { height: 8, width: 8 },
    "&::-webkit-scrollbar-track": { bgcolor: "#f1f1f1", borderRadius: "4px" },
    "&::-webkit-scrollbar-thumb": {
  bgcolor: "#d97706",
  borderRadius: "4px",
},
"&::-webkit-scrollbar-thumb:hover": {
  bgcolor: "#b45309",
},

  }}
>
  <Table stickyHeader sx={{ tableLayout: "fixed", minWidth: "100%" }}>
    <TableHead>
     <TableRow
  sx={{
    background: "linear-gradient(145deg, #f59e0b 0%, #d97706 100%)",
    "& .MuiTableCell-head": {
      backgroundColor: "transparent",
      color: "white",
      fontWeight: 700,
      textAlign: "center",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
  }}
>

        <TableCell
          sx={{
            width: 90,
            maxWidth: 90,
            minWidth: 36,
          }}
        >
          Serial
        </TableCell>
        <TableCell
          sx={{
            textAlign: "left", // keep name left-aligned
          }}
        >
          Student Name
        </TableCell>
        <TableCell
          sx={{
            width: 100,
            maxWidth: 100,
            minWidth: 48,
          }}
        >
          Roll No
        </TableCell>
        <TableCell>
          Stream
        </TableCell>
        <TableCell
          sx={{
            minWidth: 320,
          }}
        >
          Attendance
        </TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {filteredStudents.map((student, index) => (
        <TableRow
          key={student.studentId}
          sx={{
            "&:nth-of-type(even)": { bgcolor: "rgba(248, 249, 250, 0.6)" },
            "&:hover": { bgcolor: "rgba(103, 126, 234, 0.08)" },
          }}
        >
          <TableCell sx={{ textAlign: "center", width: 90, maxWidth: 90, minWidth: 36 }}>
            {index + 1}
          </TableCell>
          <TableCell sx={{ textAlign: "center" }} >{student.studentName}</TableCell>
          <TableCell sx={{ textAlign: "center", width: 100, maxWidth: 100, minWidth: 48 }}>
            {student.rollNo}
          </TableCell>
          <TableCell sx={{ textAlign: "center" }}>{student.stream}</TableCell>
          <TableCell sx={{ textAlign: "center" }}>
            <RadioGroup
              row
              value={student.attendance || ""}
              onChange={(e) => handleAttendanceChange(student.studentId, e.target.value)}
              sx={{ justifyContent: "center", gap: 2, flexWrap: "wrap" }}
            >
              <FormControlLabel
                value="Present"
                control={<Radio color="success" />}
                label="Present"
                sx={{
                  "&:hover": { transform: "scale(1.05)", transition: "all 0.15s ease-in-out" },
                }}
              />
              <FormControlLabel
                value="Absent"
                control={<Radio color="error" />}
                label="Absent"
                sx={{
                  "&:hover": { transform: "scale(1.05)", transition: "all 0.15s ease-in-out" },
                }}
              />
              <FormControlLabel
                value="Week Off"
                control={<Radio color="warning" />}
                label="Week Off"
                sx={{
                  "&:hover": { transform: "scale(1.05)", transition: "all 0.15s ease-in-out" },
                }}
              />
            </RadioGroup>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
</TableContainer>

                  <Stack direction="row" justifyContent="center" spacing={3} mt={5}>
                    <Button
                      variant="contained"
                      onClick={handleCancel}
                      sx={{
                        minWidth: 150,
                        py: 1.5,
                       
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "1.1rem",
                        background: "linear-gradient(145deg, #ef4444 0%, #dc2626 100%)",
"&:hover": {
  background: "linear-gradient(145deg, #dc2626 0%, #b91c1c 100%)",
},

                      }}
                    >
                      Cancel
                    </Button>
                    <Button
  variant="contained"
  onClick={handleSubmitAttendance}
  sx={{
    minWidth: 180,
    py: 1.5,
    color: "#fff",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "1.1rem",
    background: "linear-gradient(145deg, #f59e0b 0%, #d97706 100%)",
    boxShadow: "0 6px 16px rgba(217,119,6,0.35)",
    "&:hover": {
      background: "linear-gradient(145deg, #d97706 0%, #b45309 100%)",
      boxShadow: "0 8px 20px rgba(217,119,6,0.45)",
      transform: "translateY(-2px)",
    },
  }}
>
  Save Attendance
</Button>

                  </Stack>
                </>
              ) : selectedSection && selectedClass ? (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <Typography variant="h6" color="text.secondary">
                    No students found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {selectedStream !== "0"
                      ? `No students in stream: ${selectedStream}`
                      : "No students available for the selected filters"}
                  </Typography>
                </Box>
              ) : null}
            </CardContent>
          </Card>
        </Stack>
      </Fade>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AttendanceForm;